import { v4 as uuidv4 } from 'uuid';

import {
  type ActiveCohortBuilderItemLegacy,
  type FilterLegacy,
  FilterTypeLegacy,
  type InputFieldConfig,
  type InputOptionContextDataLegacy,
} from '@verantos-dev/types-cohorts';
import {
  Demographic_event_criteriaContext,
  Event_count_criteria_errorContext,
  Event_count_criteriaContext,
  Subjects_variableContext,
} from '@verantos-dev/util-cohort-builder-typescript-runtime';

import { getNextFilters } from './getNextFilters';

export const getActiveCohortBuilderItemStateForSubjectVariable = (
  subjectsVariableContext: Subjects_variableContext,
  codeCompletionOptions: string[][],
  nextActiveCohortBuilderItem: ActiveCohortBuilderItemLegacy,
  inputOptionContextData: InputOptionContextDataLegacy
): ActiveCohortBuilderItemLegacy => {
  // Map the subject variable context to the options
  const demographicContext = subjectsVariableContext.demographic_event_criteria();
  const countContext = subjectsVariableContext.event_count_criteria();
  const countErrorContext = subjectsVariableContext.event_count_criteria_error();

  // There are two paths for subject variables: demographic and count
  if (demographicContext) {
    return getDemographicContext(
      demographicContext,
      codeCompletionOptions,
      nextActiveCohortBuilderItem,
      inputOptionContextData
    );
  }
  if (countContext) {
    return getCountContext(
      countContext,
      codeCompletionOptions,
      nextActiveCohortBuilderItem,
      inputOptionContextData
    );
  }
  if (countErrorContext) {
    return getCountErrorContext(
      countErrorContext,
      codeCompletionOptions,
      nextActiveCohortBuilderItem,
      inputOptionContextData
    );
  }

  return nextActiveCohortBuilderItem;
};

const getDemographicContext = (
  demographicContext: Demographic_event_criteriaContext,
  codeCompletionOptions: string[][],
  nextActiveCohortBuilderItem: ActiveCohortBuilderItemLegacy,
  inputOptionContextData: InputOptionContextDataLegacy
): ActiveCohortBuilderItemLegacy => {
  // if no filters exists, return the subject variable as is
  const demographicEventFilters = demographicContext.demographic_event_filters();

  if (demographicEventFilters) {
    /** Find demographic filter sets */

    const { conceptFilters, numericFilters } = nextActiveCohortBuilderItem;
    // If concept filters exist, update the subject variable input fields
    let nextConceptFilters: FilterLegacy[] = [];
    let nextNumericFilters: FilterLegacy[] = [];
    let nextConceptInputFieldConfigMap: Record<string, InputFieldConfig> = {};
    let nextNumericInputFieldConfigMap: Record<string, InputFieldConfig> = {};
    if (conceptFilters.length > 0) {
      const conceptFilterContexts = conceptFilters.map((_, index) => {
        const filter = demographicEventFilters.demographic_event_filter(index);
        if (!filter) {
          throw new Error('Filter not found');
        }
        return filter;
      });
      const { nextFilters, nextInputFieldConfigMap } = getNextFilters(
        FilterTypeLegacy.CONCEPT,
        conceptFilters,
        conceptFilterContexts,
        codeCompletionOptions,
        inputOptionContextData
      );
      nextConceptFilters = nextFilters;
      nextConceptInputFieldConfigMap = nextInputFieldConfigMap;
    }
    if (numericFilters.length > 0) {
      const numericFilterContexts = numericFilters.map((_, index) => {
        const filter = demographicEventFilters.demographic_event_filter(
          index + conceptFilters.length
        );
        if (!filter) {
          throw new Error('Filter not found');
        }
        return filter;
      });
      const { nextFilters, nextInputFieldConfigMap } = getNextFilters(
        FilterTypeLegacy.NUMERIC,
        numericFilters,
        numericFilterContexts,
        codeCompletionOptions,
        inputOptionContextData
      );
      nextNumericFilters = nextFilters;
      nextNumericInputFieldConfigMap = nextInputFieldConfigMap;
    }

    return {
      ...nextActiveCohortBuilderItem,
      conceptFilters: nextConceptFilters,
      numericFilters: nextNumericFilters,
      inputFieldConfigMap: {
        ...nextNumericInputFieldConfigMap,
        ...nextConceptInputFieldConfigMap,
      },
    };
  }

  return nextActiveCohortBuilderItem;
};

const getCountContext = (
  countContext: Event_count_criteriaContext,
  codeCompletionOptions: string[][],
  nextActiveCohortBuilderItem: ActiveCohortBuilderItemLegacy,
  inputOptionContextData: InputOptionContextDataLegacy
): ActiveCohortBuilderItemLegacy => {
  const { countFilter } = nextActiveCohortBuilderItem;

  const { nextFilters, nextInputFieldConfigMap } = getNextFilters(
    FilterTypeLegacy.COUNT,
    [
      countFilter
        ? countFilter
        : {
            id: uuidv4(),
            inputFields: [],
          },
    ],
    [countContext],
    codeCompletionOptions,
    inputOptionContextData
  );

  return {
    ...nextActiveCohortBuilderItem,
    countFilter: nextFilters[0],
    inputFieldConfigMap: {
      ...nextInputFieldConfigMap,
    },
  };
};

const getCountErrorContext = (
  countErrorContext: Event_count_criteria_errorContext,
  codeCompletionOptions: string[][],
  nextActiveCohortBuilderItem: ActiveCohortBuilderItemLegacy,
  inputOptionContextData: InputOptionContextDataLegacy
): ActiveCohortBuilderItemLegacy => {
  const { countFilter } = nextActiveCohortBuilderItem;

  const { nextFilters, nextInputFieldConfigMap } = getNextFilters(
    FilterTypeLegacy.COUNT,
    [
      countFilter
        ? countFilter
        : {
            id: uuidv4(),
            inputFields: [],
          },
    ],
    [countErrorContext],
    codeCompletionOptions,
    inputOptionContextData
  );

  return {
    ...nextActiveCohortBuilderItem,
    countFilter: nextFilters[0],
    inputFieldConfigMap: {
      ...nextInputFieldConfigMap,
    },
  };
};
